import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";

const EngagementModelsSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="w-full flex flex-col items-center relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 flex-shrink-0"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3 border border-blue-100">
            <Sparkles className="w-4 h-4" />
            <span>2 Engagement Models</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-2 tracking-tight leading-tight">
            Choose the best{" "}
            <span className="text-blue-600">engagement model</span> for you
          </h2>
          <p className="text-sm text-slate-500 max-w-2xl mx-auto">
            Both plans include full access to the entire Asha AI Operating
            System. Choose the model that fits your practice best.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="w-full grid grid-cols-2 gap-0 items-stretch">
          {/* Managed Service */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-white rounded-l-3xl border border-slate-200 p-8 flex flex-col"
          >
            {/* Plan Header */}
            <div className="mb-5">
              <p className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-1">
                Option 1
              </p>
              <h3 className="text-2xl font-bold text-slate-900 mb-1">
                Managed Service
              </h3>
              <p className="text-sm text-slate-400">
                AI + human expert oversight
              </p>
            </div>

            {/* Price */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 px-5 py-4 mb-5">
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
                  % based pricing
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Percentage-based so our incentives stay aligned with yours
              </p>
            </div>

            {/* Description */}
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              Comes with a{" "}
              <span className="font-bold text-slate-900">
                US-based dental billing expert
              </span>{" "}
              with 8+ years of experience advising offices who works with you to strategically
              improve your collection rate and fills in where AI has gaps.
            </p>

            {/* Features */}
            <div className="space-y-2.5 flex-1">
              {[
                "Full AI OS — all agents, PMS, comms, imaging",
                "Human expert reviews all AI work",
                "Includes white-glove PMS migration to Asha",
                "Human handles complex cases & edge cases",
                "Percentage pricing = we help you collect more",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600">{text}</span>
                </div>
              ))}
            </div>

            {/* Best For */}
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Best For
              </p>
              <p className="text-sm font-semibold text-slate-900">
                Practices wanting hands-off billing with expert oversight
              </p>
            </div>
          </motion.div>

          {/* Flexible Plan - Featured */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-[#0f172a] rounded-r-3xl p-8 flex flex-col text-white relative overflow-hidden"
          >
            {/* Popular badge */}
            <div className="absolute top-5 right-5">
              <span className="bg-blue-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg">
                POPULAR
              </span>
            </div>

            {/* Plan Header */}
            <div className="mb-5">
              <p className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-1">
                Option 2
              </p>
              <h3 className="text-2xl font-bold text-white mb-1">
                Flexible Plan
              </h3>
              <p className="text-sm text-slate-400">
                AI-powered, your team reviews
              </p>
            </div>

            {/* Price */}
            <div className="bg-white/5 rounded-2xl ring-1 ring-white/10 px-5 py-4 mb-5">
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl font-extrabold text-white tracking-tight">
                  Fixed fee
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Fixed per-patient fee — predictable cost that drops as you grow
              </p>
            </div>

            {/* Description */}
            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              Get the{" "}
              <span className="font-bold text-white">
                full AI Operating System for a fixed per-patient fee
              </span>
              . Instead of our human biller reviewing AI work, your staff
              jumps in to review and fill gaps.
            </p>

            {/* Features */}
            <div className="space-y-2.5 flex-1">
              {[
                "Full AI OS — all agents, PMS, comms, imaging",
                "Only available on Asha's PMS (migration required)",
                "Your staff reviews AI work instead of our biller",
                "Cost per patient drops as practice grows",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-300">{text}</span>
                </div>
              ))}
            </div>

            {/* Best For */}
            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                Best For
              </p>
              <p className="text-sm font-semibold text-white">
                Practices wanting AI power, but with their team to fill in gaps
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EngagementModelsSlide;
